import { FiEdit2, FiInfo, FiTrash2, FiX } from "react-icons/fi";
import {
  AttributeTemplateItem,
  cloneAttributeTemplate,
} from "./AttributeTemplateTypes";
import "./AttributeTemplateDrawer.scss";

interface AttributeTemplateDrawerProps {
  show: boolean;
  template: AttributeTemplateItem | null;
  onClose: () => void;
  onEdit?: () => void;
  onDelete: () => void;
}

const AttributeTemplateDrawer = ({
  show,
  template,
  onClose,
  onEdit,
  onDelete,
}: AttributeTemplateDrawerProps) => {
  const activeTemplate = template ? cloneAttributeTemplate(template) : null;

  if (!show || !activeTemplate) {
    return null;
  }

  return (
    <div className="attribute-template-drawer-overlay">
      <div className="attribute-template-drawer" role="dialog" aria-modal="true">
        <div className="attribute-template-drawer-header">
          <div>
            <div className="attribute-template-drawer-eyebrow">Template details</div>
            <h3>{activeTemplate.name}</h3>
            <p>{activeTemplate.description}</p>
          </div>
          <button
            type="button"
            className="close-button"
            onClick={onClose}
            aria-label="Close attribute template drawer"
          >
            <FiX size={22} />
          </button>
        </div>

        <div className="attribute-template-drawer-content">
          <section className="attribute-template-drawer-section">
            <div className="attribute-template-drawer-section-heading">
              <h4>Template information</h4>
              <span className={`attribute-template-status-pill ${activeTemplate.isActive ? "active" : "inactive"}`}>
                {activeTemplate.isActive ? "Active" : "Inactive"}
              </span>
            </div>

            <div className="attribute-template-detail-stack">
              <div className="attribute-template-detail-row">
                <span className="attribute-template-detail-label">Name</span>
                <strong className="attribute-template-detail-value">
                  {activeTemplate.name}
                </strong>
              </div>

              <div className="attribute-template-detail-row">
                <span className="attribute-template-detail-label">Description</span>
                <strong className="attribute-template-detail-value description">
                  {activeTemplate.description}
                </strong>
              </div>
            </div>
          </section>

          <section className="attribute-template-drawer-section">
            <div className="attribute-template-drawer-section-heading">
              <h4>Attributes</h4>
              <span>
                {activeTemplate.attributes.length} field
                {activeTemplate.attributes.length === 1 ? "" : "s"}
              </span>
            </div>

            <div className="attribute-template-attribute-list">
              {activeTemplate.attributes.map((attribute) => (
                <article
                  key={attribute.id}
                  className="attribute-template-attribute-card"
                >
                  <div className="attribute-template-attribute-card-header">
                    <div>
                      <div className="attribute-template-attribute-name">
                        {attribute.name}
                      </div>
                      <div className="attribute-template-attribute-subtext">
                        {attribute.code}
                      </div>
                    </div>

                    <span
                      className={`attribute-template-attribute-required ${
                        attribute.isRequired ? "required" : "optional"
                      }`}
                    >
                      {attribute.isRequired ? "Required" : "Optional"}
                    </span>
                  </div>

                  <div className="attribute-template-attribute-options">
                    <div className="attribute-template-attribute-options-label">
                      <FiInfo size={14} />
                      Options
                    </div>

                    <div className="attribute-template-attribute-option-list read-mode">
                      {attribute.options.length > 0 ? (
                        attribute.options.map((option) => (
                          <span
                            key={option.id}
                            className="attribute-template-option-badge"
                          >
                            {option.label}
                          </span>
                        ))
                      ) : (
                        <span className="attribute-template-attribute-option-empty">
                          No options configured
                        </span>
                      )}
                    </div>
                  </div>
                </article>
              ))}
            </div>
          </section>
        </div>

        <div className="attribute-template-drawer-footer">
          <button
            type="button"
            className="attribute-template-edit-btn"
            onClick={onEdit}
          >
            <FiEdit2 size={16} />
            Edit
          </button>
          <button
            type="button"
            className="attribute-template-delete-btn"
            aria-label={`Delete ${activeTemplate.name}`}
            onClick={onDelete}
          >
            <FiTrash2 size={16} />
          </button>
        </div>
      </div>
    </div>
  );
};

export default AttributeTemplateDrawer;
